import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, MapPin, DollarSign, Clock, CheckCircle, X, LogOut, Navigation, 
  Camera, Upload, Truck, AlertCircle, Phone, MessageSquare, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';
import { getAuthHeaders, getUser, logout } from '../utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DriverDashboard = () => {
  const navigate = useNavigate();
  const user = getUser();
  const [profile, setProfile] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [offeredJobs, setOfferedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAvailable, setIsAvailable] = useState(true);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const [uploadType, setUploadType] = useState('pickup'); // pickup or delivery
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!user) {
      navigate('/driver/login');
      return;
    }
    loadDriverData();
    // Update location periodically
    const locationInterval = setInterval(updateLocation, 30000);
    // Refresh jobs every 10 seconds
    const dataInterval = setInterval(loadDriverData, 10000);
    
    // Get initial location
    updateLocation();
    
    return () => {
      clearInterval(locationInterval);
      clearInterval(dataInterval);
    };
  }, []);

  const updateLocation = async () => {
    if (!navigator.geolocation) return;
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          await axios.post(
            `${BACKEND_URL}/api/dispatch/drivers/location`,
            {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              speed: position.coords.speed || 0,
              heading: position.coords.heading || 0
            },
            { headers: getAuthHeaders() }
          );
        } catch (error) {
          console.error('Error updating location:', error);
        }
      },
      (error) => console.log('Location access denied:', error),
      { enableHighAccuracy: true }
    );
  };

  const loadDriverData = async () => {
    try {
      const [profileRes, jobsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/dispatch/driver/profile`, { headers: getAuthHeaders() }),
        axios.get(`${BACKEND_URL}/api/dispatch/jobs`, { headers: getAuthHeaders() })
      ]);
      
      setProfile(profileRes.data.profile);
      setIsAvailable(profileRes.data.profile.is_available);
      
      const allJobs = jobsRes.data.jobs || [];
      
      // Find active job (accepted or in_transit)
      const active = allJobs.find(j => ['accepted', 'in_transit'].includes(j.status));
      setActiveJob(active || null);
      
      // Find offered jobs
      const offered = allJobs.filter(j => j.status === 'offered');
      setOfferedJobs(offered);
      
      setJobs(allJobs);
    } catch (error) {
      console.error('Error loading driver data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleAvailability = async () => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/dispatch/driver/availability`,
        null,
        { 
          headers: getAuthHeaders(),
          params: { is_available: !isAvailable }
        }
      );
      setIsAvailable(!isAvailable);
    } catch (error) {
      console.error('Error updating availability:', error);
    }
  };

  const respondToJob = async (jobId, accept) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/dispatch/jobs/respond`,
        null,
        {
          headers: getAuthHeaders(),
          params: { job_id: jobId, accept }
        }
      );
      loadDriverData();
    } catch (error) {
      console.error('Error responding to job:', error);
      alert(error.response?.data?.detail || 'Failed to respond to job');
    }
  };

  const updateJobStatus = async (jobId, newStatus) => {
    setUpdatingStatus(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/dispatch/jobs/update-status`,
        {
          job_id: jobId,
          status: newStatus
        },
        { headers: getAuthHeaders() }
      );
      loadDriverData();
    } catch (error) {
      console.error('Error updating job status:', error);
      alert(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadProofPhoto = async () => {
    if (!photoPreview || !activeJob) return;
    
    setUploadingPhoto(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/dispatch/jobs/proof-of-delivery`,
        {
          job_id: activeJob.id,
          photo_type: uploadType,
          photo_data: photoPreview,
          recipient_name: recipientName,
          notes: deliveryNotes
        },
        { headers: getAuthHeaders() }
      );
      
      setShowPhotoUpload(false);
      setPhotoPreview(null);
      setDeliveryNotes('');
      setRecipientName('');
      
      // If this was a delivery photo, mark job as delivered
      if (uploadType === 'delivery') {
        await updateJobStatus(activeJob.id, 'delivered');
      }
      
      loadDriverData();
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/driver/login');
  };

  const openNavigation = (location) => {
    if (!location) return;
    const address = `${location.address}, ${location.city}, ${location.state} ${location.zip_code}`;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
    window.open(url, '_blank');
  };

  const callContact = (phone) => {
    if (phone) {
      window.location.href = `tel:${phone}`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-center">
          <Truck className="h-16 w-16 text-blue-400 animate-bounce mx-auto mb-4" />
          <p className="text-slate-300 text-lg">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <div className="bg-slate-800 border-b border-slate-700 px-4 py-3 sticky top-0 z-40">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-gradient-to-br from-blue-600 to-blue-400 p-2 rounded-xl">
              <Truck className="h-6 w-6 text-white" />
            </div>
            <div>
              <div className="font-bold text-white text-sm">SABEY LOGISTICS</div>
              <div className="text-xs text-slate-400">Driver Portal</div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button onClick={loadDriverData} className="p-2 hover:bg-slate-700 rounded-lg">
              <RefreshCw className="h-5 w-5 text-slate-400" />
            </button>
            <button onClick={handleLogout} className="p-2 hover:bg-slate-700 rounded-lg">
              <LogOut className="h-5 w-5 text-slate-400" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-4 pb-24">
        {/* Profile & Status Card */}
        <Card className="p-4 bg-slate-800 border-slate-700">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-white">{profile?.full_name}</h2>
              <p className="text-slate-400 text-sm">{profile?.vehicle_type} • {profile?.vehicle_number}</p>
            </div>
            <button
              onClick={toggleAvailability}
              className={`px-4 py-2 rounded-full font-semibold text-sm transition-all ${
                isAvailable
                  ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                  : 'bg-slate-600/50 text-slate-400 border border-slate-600'
              }`}
            >
              {isAvailable ? '● Online' : '○ Offline'}
            </button>
          </div>
          
          <div className="grid grid-cols-3 gap-3 pt-3 border-t border-slate-700">
            <div className="text-center">
              <div className="text-2xl font-bold text-white">{profile?.total_deliveries || 0}</div>
              <div className="text-xs text-slate-400">Deliveries</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-400">${(profile?.total_earnings || 0).toFixed(0)}</div>
              <div className="text-xs text-slate-400">Earnings</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-400">{profile?.rating || 5.0}</div>
              <div className="text-xs text-slate-400">Rating</div>
            </div>
          </div>
        </Card>

        {/* Active Job Card */}
        {activeJob && (
          <Card className="bg-gradient-to-br from-blue-900/50 to-slate-800 border-blue-500/30 overflow-hidden">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <span className="text-xs text-blue-400 font-semibold">ACTIVE DELIVERY</span>
                  <div className="font-mono text-lg font-bold text-white">{activeJob.job_number}</div>
                  {activeJob.tracking_number && (
                    <div className="text-xs text-slate-400">Tracking: {activeJob.tracking_number}</div>
                  )}
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                  activeJob.status === 'accepted' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-blue-500/20 text-blue-400'
                }`}>
                  {activeJob.status === 'accepted' ? 'EN ROUTE TO PICKUP' : 'IN TRANSIT'}
                </span>
              </div>
              
              {/* Pickup Location */}
              <div className="bg-slate-800/50 rounded-xl p-3 mb-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                      <MapPin className="h-4 w-4 text-green-400" />
                    </div>
                    <span className="text-sm font-semibold text-green-400">PICKUP</span>
                  </div>
                  <div className="flex space-x-2">
                    {activeJob.pickup_location?.contact_phone && (
                      <button 
                        onClick={() => callContact(activeJob.pickup_location.contact_phone)}
                        className="p-2 bg-slate-700 rounded-lg hover:bg-slate-600"
                      >
                        <Phone className="h-4 w-4 text-slate-300" />
                      </button>
                    )}
                    <button 
                      onClick={() => openNavigation(activeJob.pickup_location)}
                      className="p-2 bg-blue-600 rounded-lg hover:bg-blue-500"
                    >
                      <Navigation className="h-4 w-4 text-white" />
                    </button>
                  </div>
                </div>
                <div className="text-white text-sm">{activeJob.pickup_location?.address}</div>
                <div className="text-slate-400 text-sm">{activeJob.pickup_location?.city}, {activeJob.pickup_location?.state} {activeJob.pickup_location?.zip_code}</div>
                {activeJob.pickup_location?.contact_name && (
                  <div className="text-slate-500 text-xs mt-1">Contact: {activeJob.pickup_location.contact_name}</div>
                )}
              </div>
              
              {/* Delivery Location */}
              <div className="bg-slate-800/50 rounded-xl p-3 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center">
                      <MapPin className="h-4 w-4 text-red-400" />
                    </div>
                    <span className="text-sm font-semibold text-red-400">DELIVERY</span>
                  </div>
                  <div className="flex space-x-2">
                    {activeJob.delivery_location?.contact_phone && (
                      <button 
                        onClick={() => callContact(activeJob.delivery_location.contact_phone)}
                        className="p-2 bg-slate-700 rounded-lg hover:bg-slate-600"
                      >
                        <Phone className="h-4 w-4 text-slate-300" />
                      </button>
                    )}
                    <button 
                      onClick={() => openNavigation(activeJob.delivery_location)}
                      className="p-2 bg-blue-600 rounded-lg hover:bg-blue-500"
                    >
                      <Navigation className="h-4 w-4 text-white" />
                    </button>
                  </div>
                </div>
                <div className="text-white text-sm">{activeJob.delivery_location?.address}</div>
                <div className="text-slate-400 text-sm">{activeJob.delivery_location?.city}, {activeJob.delivery_location?.state} {activeJob.delivery_location?.zip_code}</div>
                {activeJob.delivery_location?.contact_name && (
                  <div className="text-slate-500 text-xs mt-1">Contact: {activeJob.delivery_location.contact_name}</div>
                )}
              </div>

              {/* Pay & Details */}
              <div className="flex items-center justify-between mb-4 px-2">
                <div className="flex items-center space-x-4 text-sm text-slate-400">
                  <span className="flex items-center"><Clock className="h-4 w-4 mr-1" />{activeJob.estimated_duration_minutes} min</span>
                  <span className="flex items-center"><Navigation className="h-4 w-4 mr-1" />{activeJob.distance_miles} mi</span>
                </div>
                <div className="text-2xl font-bold text-green-400">${activeJob.pay_amount}</div>
              </div>

              {/* Special Instructions */}
              {activeJob.special_instructions && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 mb-4">
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="h-4 w-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-xs font-semibold text-yellow-400 mb-1">SPECIAL INSTRUCTIONS</div>
                      <div className="text-sm text-yellow-200">{activeJob.special_instructions}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Status Update Buttons */}
              <div className="space-y-2">
                {activeJob.status === 'accepted' && (
                  <>
                    <Button 
                      onClick={() => updateJobStatus(activeJob.id, 'arrived_pickup')}
                      disabled={updatingStatus}
                      className="w-full bg-yellow-600 hover:bg-yellow-500 text-white font-semibold py-3"
                    >
                      <MapPin className="h-5 w-5 mr-2" />
                      Arrived at Pickup
                    </Button>
                    <Button 
                      onClick={() => {
                        setUploadType('pickup');
                        setShowPhotoUpload(true);
                      }}
                      className="w-full bg-green-600 hover:bg-green-500 text-white font-semibold py-3"
                    >
                      <Camera className="h-5 w-5 mr-2" />
                      Confirm Pickup (Take Photo)
                    </Button>
                  </>
                )}
                
                {activeJob.status === 'in_transit' && (
                  <>
                    <Button 
                      onClick={() => updateJobStatus(activeJob.id, 'arrived_delivery')}
                      disabled={updatingStatus}
                      className="w-full bg-yellow-600 hover:bg-yellow-500 text-white font-semibold py-3"
                    >
                      <MapPin className="h-5 w-5 mr-2" />
                      Arrived at Delivery
                    </Button>
                    <Button 
                      onClick={() => {
                        setUploadType('delivery');
                        setShowPhotoUpload(true);
                      }}
                      className="w-full bg-green-600 hover:bg-green-500 text-white font-semibold py-3"
                    >
                      <Camera className="h-5 w-5 mr-2" />
                      Complete Delivery (Take Photo)
                    </Button>
                  </>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Job Offers */}
        {offeredJobs.length > 0 && (
          <div>
            <h3 className="text-lg font-bold text-white mb-3 flex items-center">
              <span className="w-2 h-2 bg-yellow-400 rounded-full mr-2 animate-pulse"></span>
              New Job Offers ({offeredJobs.length})
            </h3>
            {offeredJobs.map((job) => (
              <Card key={job.id} className="mb-4 bg-gradient-to-br from-yellow-900/30 to-slate-800 border-yellow-500/30">
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-mono text-sm font-bold text-white">{job.job_number}</div>
                    <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs font-bold animate-pulse">
                      NEW OFFER
                    </span>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-start space-x-2">
                      <MapPin className="h-4 w-4 text-green-400 flex-shrink-0 mt-1" />
                      <div className="text-sm text-slate-300">
                        {job.pickup_location?.address}, {job.pickup_location?.city}
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <MapPin className="h-4 w-4 text-red-400 flex-shrink-0 mt-1" />
                      <div className="text-sm text-slate-300">
                        {job.delivery_location?.address}, {job.delivery_location?.city}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-4 text-xs text-slate-400">
                      <span><Clock className="h-3 w-3 inline mr-1" />{job.estimated_duration_minutes} min</span>
                      <span><Navigation className="h-3 w-3 inline mr-1" />{job.distance_miles} mi</span>
                    </div>
                    <div className="text-2xl font-bold text-green-400">${job.pay_amount}</div>
                  </div>
                  
                  <div className="flex space-x-3">
                    <Button
                      onClick={() => respondToJob(job.id, true)}
                      className="flex-1 bg-green-600 hover:bg-green-500 text-white font-semibold"
                    >
                      <CheckCircle className="h-5 w-5 mr-2" />
                      Accept
                    </Button>
                    <Button
                      onClick={() => respondToJob(job.id, false)}
                      variant="outline"
                      className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/20"
                    >
                      <X className="h-5 w-5 mr-2" />
                      Decline
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* No Active Jobs */}
        {!activeJob && offeredJobs.length === 0 && (
          <Card className="p-8 bg-slate-800 border-slate-700 text-center">
            <Truck className="h-16 w-16 mx-auto mb-4 text-slate-600" />
            <h3 className="text-lg font-semibold text-white mb-2">No Active Jobs</h3>
            <p className="text-slate-400 text-sm mb-4">
              {isAvailable 
                ? "You're online and ready. New job offers will appear here." 
                : "Go online to start receiving job offers."}
            </p>
            {!isAvailable && (
              <Button onClick={toggleAvailability} className="bg-green-600 hover:bg-green-500">
                Go Online
              </Button>
            )}
          </Card>
        )}

        {/* Recent Jobs History */}
        {jobs.filter(j => j.status === 'delivered').length > 0 && (
          <div>
            <h3 className="text-lg font-bold text-white mb-3">Recent Deliveries</h3>
            {jobs.filter(j => j.status === 'delivered').slice(0, 5).map((job) => (
              <Card key={job.id} className="mb-2 p-3 bg-slate-800 border-slate-700">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-mono text-sm text-white">{job.job_number}</div>
                    <div className="text-xs text-slate-400">
                      {job.delivery_location?.city}, {job.delivery_location?.state}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-green-400 font-semibold">${job.pay_amount}</div>
                    <span className="text-xs text-slate-500">Completed</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Photo Upload Modal */}
      {showPhotoUpload && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-slate-800 border-slate-700">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">
                  {uploadType === 'pickup' ? 'Pickup Confirmation' : 'Delivery Confirmation'}
                </h3>
                <button onClick={() => {
                  setShowPhotoUpload(false);
                  setPhotoPreview(null);
                }} className="p-2 hover:bg-slate-700 rounded-lg">
                  <X className="h-5 w-5 text-slate-400" />
                </button>
              </div>

              {/* Photo Preview or Upload Button */}
              {photoPreview ? (
                <div className="relative mb-4">
                  <img src={photoPreview} alt="Preview" className="w-full rounded-xl" />
                  <button 
                    onClick={() => setPhotoPreview(null)}
                    className="absolute top-2 right-2 p-2 bg-red-600 rounded-full"
                  >
                    <X className="h-4 w-4 text-white" />
                  </button>
                </div>
              ) : (
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-slate-600 rounded-xl p-8 text-center cursor-pointer hover:border-blue-500 transition-colors mb-4"
                >
                  <Camera className="h-12 w-12 mx-auto mb-3 text-slate-500" />
                  <p className="text-slate-400">Tap to take photo or upload</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handlePhotoSelect}
                    className="hidden"
                  />
                </div>
              )}

              {uploadType === 'delivery' && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-300 mb-1">Recipient Name</label>
                    <Input
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value)}
                      placeholder="Name of person who received package"
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-300 mb-1">Delivery Notes (Optional)</label>
                    <Textarea
                      value={deliveryNotes}
                      onChange={(e) => setDeliveryNotes(e.target.value)}
                      placeholder="Left at front door, handed to receptionist, etc."
                      rows={2}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                </>
              )}

              <div className="flex space-x-3">
                <Button
                  onClick={() => {
                    setShowPhotoUpload(false);
                    setPhotoPreview(null);
                  }}
                  variant="outline"
                  className="flex-1 border-slate-600 text-slate-300"
                >
                  Cancel
                </Button>
                <Button
                  onClick={uploadProofPhoto}
                  disabled={!photoPreview || uploadingPhoto}
                  className="flex-1 bg-green-600 hover:bg-green-500"
                >
                  {uploadingPhoto ? (
                    <RefreshCw className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      <Upload className="h-5 w-5 mr-2" />
                      {uploadType === 'pickup' ? 'Confirm Pickup' : 'Complete Delivery'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DriverDashboard;
